import { useState, useEffect } from "react";
import { X, Download, Bell } from "lucide-react";
import { Button } from "../ui/button";

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Don't show if already installed or dismissed recently
    const dismissed = localStorage.getItem('sqawk_install_dismissed');
    if (dismissed) {
      const dismissedAt = parseInt(dismissed);
      // Don't show again for 7 days after dismissal
      if (Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return;
    }

    // Don't show if already in standalone mode (installed)
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (window.navigator.standalone === true) return;

    // Detect iOS
    const ua = navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(isiOS);

    // For Android/Desktop — capture the beforeinstallprompt event
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after 10 seconds so user has time to explore first
      setTimeout(() => setShowPrompt(true), 10000);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // For iOS — show manual instructions after 10 seconds
    if (isiOS) {
      setTimeout(() => setShowPrompt(true), 10000);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        localStorage.setItem('sqawk_install_dismissed', Date.now().toString());
      }
      setDeferredPrompt(null);
    }
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('sqawk_install_dismissed', Date.now().toString());
    setShowPrompt(false);
  };

  if (!showPrompt) return null;

  return (
    <div 
      className="fixed bottom-20 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:w-[380px] z-[9999] animate-in slide-in-from-bottom-4 duration-300"
      data-testid="install-prompt"
    >
      <div className="bg-slate-900/95 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-5 shadow-2xl shadow-black/40">
        <button 
          onClick={handleDismiss} 
          className="absolute top-3 right-3 text-slate-500 hover:text-slate-300 transition-colors"
          data-testid="install-prompt-dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 flex items-center justify-center shrink-0">
            <Download className="w-6 h-6 text-emerald-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold text-sm mb-1">Install sQawk</h3>
            <p className="text-slate-400 text-xs leading-relaxed mb-3">
              Add to your home screen for the best experience — instant access, push notifications, and offline support.
            </p>

            {isIOS ? (
              <div className="text-slate-300 text-xs space-y-1.5">
                <p className="flex items-center gap-2">
                  <span className="text-emerald-400 font-mono">1.</span> Tap the Share button <span className="text-slate-500">(box with arrow)</span>
                </p>
                <p className="flex items-center gap-2">
                  <span className="text-emerald-400 font-mono">2.</span> Scroll down, tap <span className="font-medium text-white">"Add to Home Screen"</span>
                </p>
                <Button 
                  size="sm" 
                  onClick={handleDismiss}
                  className="w-full mt-2 bg-slate-700/50 hover:bg-slate-700 text-slate-300 h-8 text-xs"
                >
                  Got it
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  onClick={handleInstall}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white h-8 text-xs px-4"
                  data-testid="install-prompt-install"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  Install App
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={handleDismiss}
                  className="text-slate-400 hover:text-slate-300 h-8 text-xs"
                >
                  Not now
                </Button>
              </div>
            )}

            <div className="flex items-center gap-1.5 mt-2.5 text-slate-500 text-[10px]">
              <Bell className="w-3 h-3" />
              <span>Get push notifications even when the app is closed</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
